import React, { forwardRef } from "react";
import { cx } from "./format";

const VARIANTS = {
  primary: "bg-cm-gold text-cm-gold-ink hover:bg-cm-gold-hover border-transparent",
  secondary: "bg-cm-surface-2 text-cm-text border-cm-border hover:border-cm-border-strong",
  ghost: "bg-transparent text-cm-muted border-transparent hover:bg-cm-surface-2 hover:text-cm-text",
  danger: "bg-cm-danger-soft text-cm-danger border-transparent hover:bg-cm-danger hover:text-white",
  success: "bg-cm-success-soft text-cm-success border-transparent hover:bg-cm-success hover:text-white",
  outline: "bg-transparent text-cm-gold-text border-cm-gold hover:bg-cm-gold-soft",
};

const SIZES = {
  xs: "h-7 px-2.5 text-xs gap-1.5",
  sm: "h-8 px-3 text-[13px] gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-5 text-[15px] gap-2",
};

export const Spinner = ({ size = 14, className }) => (
  <span
    role="status"
    aria-label="Loading"
    className={cx("inline-block rounded-full border-2 border-current border-r-transparent animate-spin", className)}
    style={{ width: size, height: size }}
  />
);

/**
 * One button for the whole app. Keep a single `primary` (gold) per screen:
 * it's the action the page exists for.
 */
export const Button = forwardRef(function Button(
  { variant = "primary", size = "md", icon: Icon, iconRight: IconRight, loading = false, block = false,
    className, children, disabled, type = "button", ...rest },
  ref,
) {
  const iconSize = size === "lg" ? 18 : size === "xs" ? 13 : 16;
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cx(
        "inline-flex items-center justify-center whitespace-nowrap rounded-cm border font-semibold transition-colors duration-150",
        "focus-visible:outline-none focus-visible:shadow-cm-focus active:translate-y-px",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:translate-y-0",
        VARIANTS[variant] || VARIANTS.primary,
        SIZES[size] || SIZES.md,
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={iconSize - 2} /> : Icon ? <Icon size={iconSize} aria-hidden="true" /> : null}
      {children}
      {IconRight && !loading ? <IconRight size={iconSize} aria-hidden="true" /> : null}
    </button>
  );
});

/** Square icon-only button. `label` is required for screen readers. */
export const IconButton = forwardRef(function IconButton(
  { icon: Icon, label, variant = "secondary", size = "md", className, type = "button", ...rest },
  ref,
) {
  const dim = { xs: "w-7 h-7", sm: "w-8 h-8", md: "w-9 h-9", lg: "w-11 h-11" }[size] || "w-9 h-9";
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        "inline-flex items-center justify-center rounded-cm border transition-colors shrink-0",
        "focus-visible:outline-none focus-visible:shadow-cm-focus disabled:opacity-50 disabled:cursor-not-allowed",
        VARIANTS[variant] || VARIANTS.secondary,
        dim,
        className,
      )}
      {...rest}
    >
      <Icon size={size === "lg" ? 20 : size === "xs" ? 14 : 16} aria-hidden="true" />
    </button>
  );
});

export default Button;
